import { Section } from "../shared";
import MasterToggle from "./MasterToggle";
import SpendingLimits from "./SpendingLimits";
import ChainToggles from "./ChainToggles";
import TokenAllowlist from "./TokenAllowlist";
import FeatureToggles from "./FeatureToggles";
import PaymentProtocols from "./PaymentProtocols";
import AddressRules from "./AddressRules";
import { updateMcpConfig } from "../../../api/mcpApi";
import { toast } from "react-hot-toast";

export default function ConfigSection({
  config,
  spending,
  onConfigUpdate,
  onConfigChanged,
}) {
  if (!config) return null;

  const enabled = config.enabled ?? true;

  const handleMasterToggle = (val) => {
    onConfigUpdate(
      () => ({ enabled: val }),
      { mcp_enabled: val }
    );
  };

  const handleToggleChain = (chainId) => {
    const current = config.allowedChains || [];
    const next = current.includes(chainId)
      ? current.filter((c) => c !== chainId)
      : [...current, chainId];
    onConfigUpdate(
      () => ({ allowedChains: next }),
      { allowed_chains: { [chainId.toUpperCase()]: { enabled: !current.includes(chainId) } } }
    );
  };

  const handleToggleToken = (tokenId) => {
    const current = config.allowedTokens || [];
    const next = current.includes(tokenId)
      ? current.filter((t) => t !== tokenId)
      : [...current, tokenId];
    onConfigUpdate(
      () => ({ allowedTokens: next }),
      { allowedTokens: next }
    );
  };

  const handleToggleFeature = (featureId, val) => {
    onConfigUpdate(
      (prev) => ({ features: { ...prev.features, [featureId]: val } }),
      { features: { [featureId]: val } }
    );
  };

  const handleModeChange = (mode) => {
    onConfigUpdate(
      () => ({ tokenMode: mode }),
      { tokenMode: mode }
    );
  };

  const handleSaveLimits = async (limits) => {
    try {
      await updateMcpConfig({ spendingLimits: limits });
      toast.success("Spending limits saved");
      onConfigChanged();
    } catch {
      toast.error("Failed to save limits");
    }
  };

  const handleSaveProtocols = async (protocols) => {
    try {
      await updateMcpConfig({ payment_protocols: protocols });
      toast.success("Protocols saved");
      onConfigChanged();
    } catch {
      toast.error("Failed to save protocols");
    }
  };

  const handleUpdateAddresses = async (updates) => {
    try {
      await updateMcpConfig({
        addressRules: { ...config.addressRules, ...updates },
      });
      onConfigChanged();
    } catch {
      toast.error("Failed to save address rules");
    }
  };

  return (
    <Section
      title="Advanced Configuration"
      subtitle="Fine-tune what your AI agents can do"
      id="mcp-config"
    >
      <MasterToggle enabled={enabled} onChange={handleMasterToggle} />

      <SpendingLimits
        limits={config.spendingLimits}
        onSave={handleSaveLimits}
        disabled={!enabled}
      />

      <ChainToggles
        allowedChains={config.allowedChains}
        onToggle={handleToggleChain}
        disabled={!enabled}
      />

      <TokenAllowlist
        mode={config.tokenMode || "allowlist"}
        allowedTokens={config.allowedTokens}
        onModeChange={handleModeChange}
        onToggleToken={handleToggleToken}
        disabled={!enabled}
      />

      <FeatureToggles
        features={config.features}
        onToggle={handleToggleFeature}
        disabled={!enabled}
      />

      <PaymentProtocols
        protocols={config.protocols}
        onSave={handleSaveProtocols}
        disabled={!enabled}
      />

      <AddressRules
        config={config.addressRules}
        onUpdate={handleUpdateAddresses}
        disabled={!enabled}
      />
    </Section>
  );
}
